import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { 
  Users, Clock, LogOut, MapPin, Calendar, Download, 
  Edit, AlertCircle, CheckCircle, XCircle, Briefcase
} from 'lucide-react';

import axios from 'axios';
import { formatDateTimeWithTimezone, formatTimeWithTimezone } from '../utils/timezone';
import TeamScheduleManager from '../components/TeamScheduleManager';
import TeamBulkTimeEditDialog from '../components/TeamBulkTimeEditDialog';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const Teams = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin, isSupervisor } = useAuth();
  const { toast } = useToast();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showClockInDialog, setShowClockInDialog] = useState(false);
  const [clockInJob, setClockInJob] = useState('');
  const [sendNotifications, setSendNotifications] = useState(true);
  const [availableJobs, setAvailableJobs] = useState([]);

  // Redirect non-admin/supervisor users. Kept in an effect (not a render-time
  // early return) so every hook below always runs — an early return here
  // crashed with React #310 on refresh while auth was still hydrating.
  useEffect(() => {
    if (user && !isAdmin && !isSupervisor) navigate('/home');
  }, [user, isAdmin, isSupervisor, navigate]);

  const handleSelectTeam = useCallback(async (teamId) => {
    setSelectedTeam(teamId);
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/teams/${teamId}/members-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data.members || []);
      setTempMembers(response.data.temp_members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      
      // Auto-select first team for supervisors (supports multi-supervisor teams)
      if (isSupervisor && response.data.length > 0) {
        const supervisorTeam = response.data.find(
          t => (t.supervisor_ids || []).includes(user.id) || t.supervisor_id === user.id
        );
        if (supervisorTeam) {
          handleSelectTeam(supervisorTeam.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to load teams',
        variant: 'destructive'
      });
    }
  }, [token, isSupervisor, user, handleSelectTeam, toast]);

  const fetchAvailableJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for in-progress jobs
      const inProgressJobs = response.data.filter(j => j.status === 'in_progress');
      setAvailableJobs(inProgressJobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchTeams();
      fetchAvailableJobs();
    }
  }, [token, fetchTeams, fetchAvailableJobs]);

  // Member selection for selective team clock-in/out (Jun 2026)
  const [clockSelected, setClockSelected] = useState(new Set());
  const [showClockOutDialog, setShowClockOutDialog] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  // Temp/mutual coverage (Aug 2026): shift-only extra workers
  const [tempMembers, setTempMembers] = useState([]);
  const [tempAdds, setTempAdds] = useState([]);
  const [tempQuery, setTempQuery] = useState('');
  const [allWorkers, setAllWorkers] = useState([]);

  const openClockIn = async () => {
    let preset = new Set(teamMembers.filter((m) => !m.is_clocked_in).map((m) => m.id));
    try {
      const { data } = await axios.get(`${API}/teams/${selectedTeam}/on-shift-now`, { headers: { Authorization: `Bearer ${token}` } });
      setHasSchedule(!!data.has_schedule);
      if (data.member_ids !== null) {
        const onShift = new Set(data.member_ids);
        preset = new Set(teamMembers.filter((m) => onShift.has(m.id) && !m.is_clocked_in).map((m) => m.id));
      }
    } catch (e) { setHasSchedule(false); }
    setClockSelected(preset);
    setTempAdds([]);
    setTempQuery('');
    try {
      const res = await axios.get(`${API}/users?scope=all`, { headers: { Authorization: `Bearer ${token}` } });
      setAllWorkers(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setAllWorkers([]); }
    setShowClockOutDialog(false);
    setShowClockInDialog(true);
  };

  const openClockOut = () => {
    setClockSelected(new Set([
      ...teamMembers.filter((m) => m.is_clocked_in),
      ...tempMembers.filter((t) => t.is_clocked_in),
    ].map((m) => m.id)));
    setShowClockInDialog(false);
    setShowClockOutDialog(true);
  };

  const toggleMember = (id) => setClockSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const MemberChecklist = ({ members, emptyText, disableClockedIn = false }) => {
    const eligible = disableClockedIn ? members.filter((m) => !m.is_clocked_in) : members;
    return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Who&apos;s on this shift? ({clockSelected.size}/{eligible.length})
        </Label>
        <div className="flex gap-2 text-xs">
          <button type="button" className="text-purple-500 hover:underline" onClick={() => setClockSelected(new Set(eligible.map((m) => m.id)))}>All</button>
          <button type="button" className="text-purple-500 hover:underline" onClick={() => setClockSelected(new Set())}>None</button>
        </div>
      </div>
      {hasSchedule && showClockInDialog && (
        <p className="text-xs text-blue-500 mb-1">Pre-selected from today&apos;s schedule — adjust as needed.</p>
      )}
      <div className="max-h-44 overflow-y-auto space-y-1 border border-gray-200 dark:border-[#2D3748] rounded-lg p-2">
        {members.length === 0 && <p className="text-xs text-slate-500">{emptyText}</p>}
        {members.map((m) => {
          const locked = disableClockedIn && m.is_clocked_in;
          return (
          <label key={m.id} className={`flex items-center gap-2 text-sm py-0.5 ${locked ? 'opacity-60' : 'cursor-pointer'}`} data-testid={`clock-member-${m.id}`}>
            <Checkbox checked={locked ? false : clockSelected.has(m.id)} disabled={locked} onCheckedChange={() => !locked && toggleMember(m.id)} />
            <span className="flex-1 text-gray-800 dark:text-gray-200">{m.name}</span>
            {m.is_clocked_in && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">on clock</span>}
          </label>
          );
        })}
      </div>
    </div>
    );
  };

  const handleTeamClockIn = async () => {
    if (!selectedTeam) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/teams/clock-in`,
        {
          team_id: selectedTeam,
          job_id: clockInJob || null,
          send_notifications: sendNotifications,
          member_ids: Array.from(clockSelected),
          extra_member_ids: tempAdds.map((t) => t.id)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Team Clocked In',
        description: `${response.data.clocked_in} member(s) clocked in successfully`,
      });
      
      // Refresh team members
      handleSelectTeam(selectedTeam);
      setShowClockInDialog(false);
      setClockInJob('');
      setTempAdds([]);
    } catch (error) {
      toast({
        title: 'Clock In Failed',
        description: error.response?.data?.detail || 'Failed to clock in team',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeamClockOut = async () => {
    if (!selectedTeam) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/teams/clock-out`,
        { team_id: selectedTeam, member_ids: Array.from(clockSelected) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowClockOutDialog(false);
      
      toast({
        title: 'Team Clocked Out',
        description: `${response.data.clocked_out} member(s) clocked out successfully`,
      });
      
      // Refresh team members
      handleSelectTeam(selectedTeam);
    } catch (error) {
      toast({
        title: 'Clock Out Failed',
        description: error.response?.data?.detail || 'Failed to clock out team',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTeamToDisplay = () => {
    if (isAdmin) {
      return teams; // Admins see all teams
    }
    // Supervisors see only their teams (supports multi-supervisor teams)
    return teams.filter(
      t => (t.supervisor_ids || []).includes(user.id) || t.supervisor_id === user.id
    );
  };

  const selectedTeamData = teams.find(t => t.id === selectedTeam);
  const clockedInCount = teamMembers.filter(m => m.is_clocked_in).length;

  // Render nothing for workers while the redirect effect above kicks in.
  // Kept below every hook so the hook order never changes (React #310 guard).
  if (user && !isAdmin && !isSupervisor) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#0F1923] pb-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#A78BFA] to-[#818CF8] text-white p-6 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                <Users className="w-8 h-8 mr-3" />
                Team Management
              </h1>
              <p className="text-white/95 font-medium mt-1">
                {isAdmin ? 'Manage all company teams' : 'Manage your team'}
              </p>
            </div>
            <Button
              onClick={() => navigate('/home')}
              variant="outline"
              className="bg-[#1A2433] text-[#A78BFA] hover:bg-[#0F1923]"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Teams List */}
        <Card className="shadow-xl">
          <CardHeader className="bg-indigo-50 dark:bg-[#1A2433]">
            <CardTitle className="text-xl flex items-center text-gray-900 dark:text-gray-100">
              <Users className="w-6 h-6 mr-2 text-[#A78BFA]" />
              {isAdmin ? 'All Teams' : 'Your Team'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getTeamToDisplay().map(team => (
                <div
                  key={team.id}
                  onClick={() => handleSelectTeam(team.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedTeam === team.id
                      ? 'border-purple-500 bg-purple-50 dark:bg-[#0F1923] shadow-lg'
                      : 'border-gray-200 dark:border-[#2D3748] hover:border-purple-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">{team.name}</h3>
                    {team.is_clocked_in && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 text-xs rounded-full font-semibold">
                        Clocked In
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {team.location}
                  </p>
                </div>
              ))}
            </div>
            {getTeamToDisplay().length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No teams found</p>
                {isSupervisor && (
                  <p className="text-sm mt-2">Contact your administrator to be assigned to a team</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Team Details */}
        {selectedTeam && selectedTeamData && (
          <>
            {/* Team Actions */}
            <Card className="shadow-xl border-2 border-[#2D3748]">
              <CardHeader className="bg-gradient-to-r from-[#A78BFA] to-[#818CF8] text-white">
                <CardTitle className="text-xl flex items-center justify-between">
                  <span>Team Actions - {selectedTeamData.name}</span>
                  <span className="text-sm font-normal">
                    {clockedInCount} / {teamMembers.length} Clocked In
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    data-testid="team-clock-in-btn"
                    onClick={openClockIn}
                    disabled={isLoading}
                    className="h-16 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-[#00C896] hover:to-emerald-700 text-white font-semibold text-lg shadow-lg"
                  >
                    <Clock className="w-6 h-6 mr-2" />
                    Clock In Team
                  </Button>
                  <Button
                    data-testid="team-clock-out-btn"
                    onClick={openClockOut}
                    disabled={isLoading}
                    className="h-16 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-semibold text-lg shadow-lg"
                  >
                    <LogOut className="w-6 h-6 mr-2" />
                    Clock Out Team
                  </Button>
                  <Button
                    data-testid="team-bulk-edit-btn"
                    onClick={() => setShowBulkEdit(true)}
                    disabled={isLoading}
                    className="h-16 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-lg shadow-lg"
                  >
                    <Edit className="w-6 h-6 mr-2" />
                    Bulk Edit Time
                  </Button>
                </div>

                {/* Clock In Dialog */}
                {showClockInDialog && (
                  <div className="mt-6 p-6 bg-purple-50 dark:bg-[#0F1923] rounded-lg border-2 border-purple-200 dark:border-[#2D3748]">
                    <h3 className="font-bold text-lg mb-4 text-purple-900 dark:text-purple-200">Clock In Options</h3>
                    
                    <div className="space-y-4">
                      <MemberChecklist members={teamMembers} emptyText="This team has no members." disableClockedIn />

                      {/* Temp/mutual coverage (Aug 2026): shift-only extras */}
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Add someone for this shift (mutual / coverage)
                        </Label>
                        <div className="relative">
                          <input
                            type="text"
                            value={tempQuery}
                            onChange={(e) => setTempQuery(e.target.value)}
                            placeholder="Search workers by name…"
                            className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-[#2D3748] rounded-lg bg-white dark:bg-[#1A2433] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            data-testid="teams-temp-search"
                          />
                          {tempQuery.trim() && (() => {
                            const rosterIds = new Set([
                              ...teamMembers.map((m) => m.id),
                              ...tempMembers.map((t) => t.id),
                              ...tempAdds.map((t) => t.id),
                            ]);
                            const results = allWorkers
                              .filter((w) => !rosterIds.has(w.id) && `${w.name || ''} ${w.email || ''}`.toLowerCase().includes(tempQuery.trim().toLowerCase()))
                              .slice(0, 8);
                            return results.length > 0 ? (
                              <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-[#1A2433] border border-gray-200 dark:border-[#2D3748] rounded-lg shadow-lg max-h-44 overflow-y-auto" data-testid="teams-temp-results">
                                {results.map((w) => (
                                  <button key={w.id} type="button"
                                          className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-[#0F1923]"
                                          data-testid={`teams-temp-pick-${w.id}`}
                                          onClick={() => { setTempAdds((prev) => [...prev, { id: w.id, name: w.name }]); setTempQuery(''); }}>
                                    {w.name} <span className="text-xs text-slate-500">{w.email}</span>
                                  </button>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                        {tempAdds.length > 0 && (
                          <div className="flex flex-wrap gap-1.5" data-testid="teams-temp-adds">
                            {tempAdds.map((t) => (
                              <span key={t.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" data-testid={`teams-temp-chip-${t.id}`}>
                                {t.name}
                                <button type="button" onClick={() => setTempAdds((prev) => prev.filter((x) => x.id !== t.id))} aria-label={`Remove ${t.name}`} data-testid={`teams-temp-remove-${t.id}`}>✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-[11px] text-slate-500">Temps punch in with the team for this shift only — the roster is unchanged.</p>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Associate with Job (Optional)
                        </Label>
                        <select
                          value={clockInJob}
                          onChange={(e) => setClockInJob(e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-gray-300 dark:border-[#2D3748] rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-[#1A2433] text-gray-900 dark:text-gray-100"
                        >
                          <option value="">No Job Selected</option>
                          {availableJobs.map(job => (
                            <option key={job.id} value={job.id}>
                              {job.title} - {job.job_address || 'No address'}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="sendNotifications"
                          checked={sendNotifications}
                          onCheckedChange={setSendNotifications}
                        />
                        <Label htmlFor="sendNotifications" className="text-sm">
                          Send email & SMS notifications to team members (includes safety topic link)
                        </Label>
                      </div>

                      <div className="flex space-x-3">
                        <Button
                          data-testid="confirm-team-clock-in-btn"
                          onClick={handleTeamClockIn}
                          disabled={isLoading || clockSelected.size === 0}
                          className="flex-1 bg-[#A78BFA] hover:bg-purple-700 text-white"
                        >
                          Confirm Clock In ({clockSelected.size})
                        </Button>
                        <Button
                          onClick={() => setShowClockInDialog(false)}
                          variant="outline"
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Clock Out Dialog — pick who's done for the day */}
                {showClockOutDialog && (
                  <div className="mt-6 p-6 bg-red-50 dark:bg-[#0F1923] rounded-lg border-2 border-red-200 dark:border-[#2D3748]" data-testid="team-clock-out-dialog">
                    <h3 className="font-bold text-lg mb-4 text-red-800 dark:text-red-300">Clock Out — who&apos;s done?</h3>
                    <div className="space-y-4">
                      <MemberChecklist
                        members={[
                          ...teamMembers.filter((m) => m.is_clocked_in),
                          ...tempMembers.filter((t) => t.is_clocked_in).map((t) => ({ ...t, name: `${t.name} (temp)` })),
                        ]}
                        emptyText="Nobody on this team is currently clocked in."
                      />
                      <div className="flex space-x-3">
                        <Button
                          data-testid="confirm-team-clock-out-btn"
                          onClick={handleTeamClockOut}
                          disabled={isLoading || clockSelected.size === 0}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                        >
                          Confirm Clock Out ({clockSelected.size})
                        </Button>
                        <Button onClick={() => setShowClockOutDialog(false)} variant="outline" className="flex-1">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bulk time correction for the whole crew (Jun 2026) */}
            <TeamBulkTimeEditDialog
              open={showBulkEdit}
              onClose={(changed) => {
                setShowBulkEdit(false);
                if (changed) handleSelectTeam(selectedTeam);
              }}
              teamId={selectedTeam}
              teamName={selectedTeamData.name}
              members={teamMembers}
              token={token}
            />

            {/* Shift schedule builder (Jun 2026) */}
            <TeamScheduleManager
              teamId={selectedTeam}
              teamName={selectedTeamData.name}
              members={teamMembers}
              jobs={availableJobs}
              token={token}
            />

            {/* Team Members List */}
            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-[#1A2433] dark:to-[#1A2433]">
                <CardTitle className="text-xl flex items-center text-gray-900 dark:text-gray-100">
                  <Users className="w-6 h-6 mr-2 text-[#818CF8]" />
                  Team Members ({teamMembers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading team members...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamMembers.map(member => (
                      <div
                        key={member.id}
                        className={`p-4 rounded-lg border-2 ${
                          member.is_clocked_in
                            ? 'border-green-200 dark:border-[#2D3748] bg-green-50 dark:bg-[#0F1923]'
                            : 'border-gray-200 dark:border-[#2D3748] bg-white dark:bg-[#1A2433]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{member.name}</h4>
                              <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                                member.is_clocked_in
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                  : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300'
                              }`}>
                                {member.is_clocked_in ? 'Clocked In' : 'Not Clocked In'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{member.email}</p>
                            {member.phone && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">{member.phone}</p>
                            )}
                            {member.is_clocked_in && member.clock_in_time && (
                              <p className="text-sm text-green-700 dark:text-green-300 mt-2 font-medium">
                                <Clock className="w-4 h-4 inline mr-1" />
                                Clocked in at: {formatTimeWithTimezone(member.clock_in_time, user?.timezone)}
                              </p>
                            )}
                          </div>
                          <div>
                            {member.is_clocked_in ? (
                              <CheckCircle className="w-8 h-8 text-green-500" />
                            ) : (
                              <XCircle className="w-8 h-8 text-gray-300" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>


    </div>
  );
};

export default Teams;
